import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, ArrowDown, Clock, X } from "lucide-react";
import { useConversationMessagesInfinite } from "@/api/hooks/use-conversation-messages";
import {
  useStreamingChat,
  useStreamingStatus,
} from "../hooks/use-streaming-chat";
import {
  getCompletedDraftAcknowledgement,
  hasCompletedDraft,
  type StreamingExternalLink,
  useStreamingStore,
} from "../store/streaming-store";
import { Message } from "@/types";
import { MessageRenderer } from "@/components/messages/MessageRenderer";
import { MessageActions } from "@/components/messages/MessageActions";
import { MessageExternalLinks } from "@/components/messages/MessageExternalLinks";
import { ComparisonButton } from "@/features/product-comparison/components/ComparisonButton";
import { WebProductCarouselWidget } from "@/components/widgets/WebProductCarouselWidget";
import { Button } from "@/components/ui/button";
import { useMessageHandlers } from "@/hooks/useMessageHandlers";
import { useCurrentChatId } from "@/hooks/use-current-chat-id";
import { MessageTransformer } from "@/api/services/message-transformer";
import {
  EmptyState,
  LoadingSpinner,
  MessageErrorBoundary,
  ShinyText,
} from "@/components/common";
import { useChatScroll } from "../hooks/use-chat-scroll";
import { useChatStore } from "../store/chat-store";
import {
  draftChatKeyFromRouteId,
  getChatKey,
  isDraftChatKey,
} from "../store/streaming-store";
import { useChatAccessStore } from "../store/chat-access-store";
import { AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cancelRedirect } from "../redirect-navigation";
import { useChatScope } from "../scope/chat-scope-context";
import { cn } from "@/lib/utils";
import { GradientScrollbar } from "@/components/ui/gradient-scrollbar";
import type { WebSearchProduct } from "@/api/models/api-types";

const EMPTY_EXTERNAL_LINKS: StreamingExternalLink[] = [];
const EMPTY_WEB_PRODUCTS: WebSearchProduct[] = [];

/** Tam sayfa / geniş dock: mevcut tavan. Dar dock'ta CSS ile %100'e çekilir. */
const CHAT_BUBBLE_WIDTH = "max-w-[90%] md:max-w-[80%]";
const CHAT_BUBBLE_NARROW = "max-w-[70%]";

// ===== Memoized Message Row =====
// Streaming sırasında her token'da yalnızca değişen satır (streaming tail)
// yeniden render edilir; geçmiş mesajlar referans-stabil olduğu için atlanır.
interface MessageRowProps {
  message: Message;
  isLastAssistantMessage: boolean;
  onAction: (messageId: string, action: any) => void;
}

const MessageRow = React.memo<MessageRowProps>(
  ({ message, isLastAssistantMessage, onAction }) => {
    return (
      <div
        data-message-id={message.id}
        className={`flex group ${
          message.role === "user" ? "justify-end" : "justify-start"
        }`}
      >
        <div
          data-chat-bubble={message.role}
          className={cn(
            CHAT_BUBBLE_WIDTH,
            "rounded-2xl",
            message.role === "user"
              ? "chat-bubble-align-end text-foreground rounded-br-sm"
              : "text-foreground rounded-bl-sm",
          )}
        >
          <div className="px-4">
            <MessageRenderer
              message={message}
              isLastAssistantMessage={isLastAssistantMessage}
              onEdit={(messageId) => onAction(messageId, "edit")}
            />
          </div>

          {message.role === "assistant" && !message.isPending && (
            <div className="px-4 pb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <MessageActions message={message} onAction={onAction} />
            </div>
          )}
        </div>
      </div>
    );
  }
);
MessageRow.displayName = "MessageRow";

export const StreamingMessages: React.FC = () => {
  const { t } = useTranslation();
  // Route ya da ChatScope (dock) — hangisindeysek oradan
  const chatId = useCurrentChatId() ?? undefined;
  const { id: routeId } = useParams<{ id: string }>();
  const scopeChatId = useChatScope()?.chatId;
  const draftKey = isDraftChatKey(scopeChatId)
    ? scopeChatId
    : !scopeChatId
      ? draftChatKeyFromRouteId(routeId)
      : null;
  const compactBubbles = useChatScope()?.compactBubbles === true;
  const location = useLocation();
  const navigate = useNavigate();
  const shareToken = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("token");
  }, [location.search]);

  // Local UI state
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);

  // ChatGPT-style: Get input height for dynamic padding-bottom
  const inputHeight = useChatStore((state) => state.inputHeight);

  // ===== API: Infinite scroll ile mesajları çek =====
  const {
    data: infiniteData,
    isLoading: isLoadingMessages,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useConversationMessagesInfinite(chatId || null, 20, shareToken);

  // ===== Access flags (canEdit/isPublic) from infinite-scroll response =====
  const setAccess = useChatAccessStore((state) => state.setAccess);
  const canEditFromResponse = infiniteData?.pages?.[0]?.data?.canEdit;
  const isPublicFromResponse = infiniteData?.pages?.[0]?.data?.isPublic;

  useEffect(() => {
    if (!chatId) return;
    if (
      typeof canEditFromResponse === "undefined" &&
      typeof isPublicFromResponse === "undefined"
    ) {
      return;
    }
    setAccess(chatId, {
      canEdit:
        typeof canEditFromResponse === "boolean" ? canEditFromResponse : null,
      isPublic:
        typeof isPublicFromResponse === "boolean" ? isPublicFromResponse : null,
    });
  }, [chatId, canEditFromResponse, isPublicFromResponse, setAccess]);

  // ===== Streaming Session (per chat) =====
  const chatKey = useMemo(
    () => draftKey ?? getChatKey(chatId),
    [draftKey, chatId],
  );
  const session = useStreamingStore((state) => state.sessionsByChat[chatKey]);
  const clearCompletedDraft = useStreamingStore(
    (state) => state.clearCompletedDraft
  );
  const removeQueuedMessage = useStreamingStore(
    (state) => state.removeQueuedMessage
  );
  const { drainQueuedMessages } = useStreamingChat();

  const streamingContent = session?.streamingContent || "";
  const statusMessages = session?.statusMessages || [];
  const reasoningContent = session?.reasoningContent || "";
  const externalLinks = session?.externalLinks || [];
  const comparison = session?.comparison ?? null;
  const webProducts = session?.webProducts || [];
  const webSearchQuery = session?.webSearchQuery || undefined;
  const streamingMessageId = session?.streamingMessageId || null;
  const serverMessageId = session?.serverMessageId || null;
  const pendingUserMessage = session?.pendingUserMessage || null;
  const pendingUserMessageId = session?.pendingUserMessageId || null;
  const pendingUserAttachments = session?.pendingUserAttachments;
  const completedUserMessageId = session?.completedUserMessageId || null;
  const completedUserMessage = session?.completedUserMessage || null;
  const completedUserAttachments = session?.completedUserAttachments;
  const completedAssistantMessageId =
    session?.completedAssistantMessageId || null;
  const completedAssistantContent = session?.completedAssistantContent || "";
  const completedExternalLinks =
    session?.completedExternalLinks ?? EMPTY_EXTERNAL_LINKS;
  const completedComparison = session?.completedComparison ?? null;
  const completedWebProducts =
    session?.completedWebProducts ?? EMPTY_WEB_PRODUCTS;
  const completedWebQuery = session?.completedWebQuery ?? undefined;
  // Sırada bekleyen (stream sürerken gönderilen) kullanıcı mesajları
  const queuedMessages = session?.queuedMessages ?? [];

  // ===== Streaming Status =====
  // İlk turda route/scope id'si henüz 24 karakter değil; useCurrentChatId
  // null döner ve status playground oturumuna bakardı. Canlı oturum draft anahtarında.
  const { isStreaming } = useStreamingStatus(chatKey);

  // Streaming tamamlandı ama content varsa hala göster (backend mesajları gelene kadar)
  const shouldShowStreamingContent = useMemo(() => {
    if (!streamingContent) return false;
    const status = session?.status ?? "idle";
    return (
      status === "streaming" || status === "completed" || status === "error"
    );
  }, [streamingContent, session?.status]);

  // ===== Transform Infinite Scroll Data to Messages =====
  // ✅ Optimized: Only recompute when pages change
  const existingMessages: Message[] = React.useMemo(() => {
    if (!infiniteData?.pages) return [];

    let backendMessages: Message[] = [];

    // Sayfaları ters sırada işle (en eski mesajlar önce)
    const reversedPages = [...infiniteData.pages].reverse();

    reversedPages.forEach((page) => {
      if (page.data?.messages) {
        // Her sayfayı Message formatına çevir (infinite scroll formatı)
        const transformedMessages =
          MessageTransformer.transformInfiniteScrollToMessages(page);
        backendMessages.push(...transformedMessages);
      }
    });

    // Mesajları timestamp'e göre sırala
    backendMessages.sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    return backendMessages;
  }, [infiniteData?.pages]); // ✅ More specific dependency

  // ===== Create streaming message for display =====
  // 🆕 Streaming content varsa ve bu chat'e aitse göster
  // Streaming tamamlandığında da content'i göster (backend mesajları gelene kadar)
  const streamingMessage = useMemo<Message | null>(() => {
    if (!streamingContent || !shouldShowStreamingContent) return null;

    return {
      id: streamingMessageId || "streaming-temp",
      content: streamingContent,
      role: "assistant",
      timestamp: new Date(),
      isOptimistic: true,
      isPending: isStreaming, // Streaming devam ediyorsa pending göster
      hasError: false,
    };
  }, [
    streamingContent,
    shouldShowStreamingContent,
    streamingMessageId,
    isStreaming,
  ]);

  // ===== Create pending user message =====
  // 🆕 Sadece bu chat'e ait pending mesaj ise göster
  // Streaming tamamlandığında da göster (backend mesajları gelene kadar)
  //
  // Backend, aktif stream'in kullanıcı mesajını initial_response ile birlikte
  // kaydeder; bir önceki mesajın senkron refetch'leri bu kopyayı stream daha
  // bitmeden listeye düşürebilir. Backend kopyası geldiyse optimistic pending
  // balonunu gizle — yoksa aynı mesaj bir anlığına çift görünür.
  const isPendingUserMessageAcked = useMemo(() => {
    if (!serverMessageId) return false;
    const backendUserMessageId = `${serverMessageId}_user`;
    return existingMessages.some((msg) => msg.id === backendUserMessageId);
  }, [serverMessageId, existingMessages]);

  const pendingUserMessageObj = useMemo<Message | null>(() => {
    if (!pendingUserMessage) return null;
    if (isPendingUserMessageAcked) return null;

    return {
      id: pendingUserMessageId || "pending-user-msg",
      content: pendingUserMessage,
      role: "user",
      timestamp: new Date(),
      isOptimistic: true,
      isPending: false,
      hasError: false,
      images:
        pendingUserAttachments && pendingUserAttachments.length > 0
          ? pendingUserAttachments
          : undefined,
    };
  }, [
    pendingUserMessage,
    pendingUserMessageId,
    pendingUserAttachments,
    isPendingUserMessageAcked,
  ]);

  // ===== Completed Draft (waiting backend sync) =====
  // Backend'in mesajı kaydettiğini iki yolla doğrula:
  // 1) initial_response ile gelen serverMessageId üzerinden ID eşleşmesi (asıl yol)
  // 2) serverMessageId hiç gelmediyse içerik bazlı fallback — draft'ı ID yok diye
  //    gizlersek cevap refetch gelene kadar ekrandan kaybolur.
  //
  // Kullanıcı ve asistan için ayrı ack: stop ile durdurulan mesajlarda backend
  // kullanıcı kopyasını hemen kaydeder ama asistan cevabı sonradan gelir;
  // asistanı beklersek kullanıcı mesajı o arada çift görünür.
  const draftAck = useMemo(
    () =>
      session
        ? getCompletedDraftAcknowledgement(existingMessages, session)
        : {
            user: undefined,
            userAcked: true,
            assistant: undefined,
            assistantAcked: true,
            complete: true,
            missing: {
              userImages: [],
              externalLinks: [],
              webProducts: [],
              webQuery: false,
              comparison: false,
            },
          },
    [existingMessages, session]
  );
  const isCompletedDraftAcked = draftAck.assistantAcked;
  const isCompletedUserDraftAcked = draftAck.userAcked;

  const completedUserMessageObj = useMemo<Message | null>(() => {
    if (!completedUserMessage) return null;
    // Backend kullanıcı satırı geldiyse eksik attachment'lar aynı satıra
    // birleştirilir; ikinci bir optimistic balon çizilmez.
    if (draftAck.user || isCompletedUserDraftAcked) return null;

    return {
      id: completedUserMessageId || "completed_user_draft",
      content: completedUserMessage,
      role: "user",
      timestamp: new Date(),
      isOptimistic: true,
      isPending: false,
      hasError: false,
      images:
        completedUserAttachments && completedUserAttachments.length > 0
          ? completedUserAttachments
          : undefined,
    };
  }, [
    completedUserMessage,
    completedUserMessageId,
    completedUserAttachments,
    draftAck.user,
    isCompletedUserDraftAcked,
  ]);

  const completedAssistantMessageObj = useMemo<Message | null>(() => {
    const hasVisibleDraft =
      completedAssistantContent !== "" ||
      completedExternalLinks.length > 0 ||
      completedWebProducts.length > 0 ||
      completedComparison !== null;
    if (!hasVisibleDraft || draftAck.assistant || isCompletedDraftAcked) {
      return null;
    }

    return {
      id: completedAssistantMessageId || "completed_assistant_draft",
      content: completedAssistantContent,
      role: "assistant",
      timestamp: new Date(),
      isOptimistic: true,
      isPending: false,
      hasError: false,
      externalLinks:
        completedExternalLinks.length > 0
          ? completedExternalLinks.map(({ url, label }) => ({ url, label }))
          : undefined,
      webProducts:
        completedWebProducts.length > 0 ? completedWebProducts : undefined,
      webSearchQuery: completedWebQuery,
      productComparison: completedComparison ?? undefined,
    };
  }, [
    completedAssistantContent,
    completedAssistantMessageId,
    completedExternalLinks,
    completedWebProducts,
    completedWebQuery,
    completedComparison,
    draftAck.assistant,
    isCompletedDraftAcked,
  ]);

  // History satırı metin olarak geldiyse fakat streamed artefaktları henüz
  // persist edilmediyse eksikleri aynı backend mesajına ekle. İkinci bir
  // assistant balonu oluşturmak cevap metnini iki kez gösteriyordu.
  const reconciledMessages = useMemo(() => {
    if (!draftAck.user && !draftAck.assistant) return existingMessages;

    return existingMessages.map((message) => {
      if (draftAck.user?.id === message.id && draftAck.missing.userImages.length) {
        return {
          ...message,
          images: [...(message.images ?? []), ...draftAck.missing.userImages],
        };
      }

      if (draftAck.assistant?.id !== message.id) return message;

      return {
        ...message,
        externalLinks:
          draftAck.missing.externalLinks.length > 0
            ? [
                ...(message.externalLinks ?? []),
                ...draftAck.missing.externalLinks.map(({ url, label }) => ({
                  url,
                  label,
                })),
              ]
            : message.externalLinks,
        webProducts:
          draftAck.missing.webProducts.length > 0
            ? [
                ...(message.webProducts ?? []),
                ...draftAck.missing.webProducts,
              ]
            : message.webProducts,
        webSearchQuery:
          draftAck.missing.webQuery && completedWebQuery
            ? completedWebQuery
            : message.webSearchQuery,
        productComparison:
          draftAck.missing.comparison && completedComparison
            ? completedComparison
            : message.productComparison,
      };
    });
  }, [existingMessages, draftAck, completedWebQuery, completedComparison]);

  // ===== Combine messages (with duplicate prevention) =====
  const allMessages = useMemo(() => {
    const messages: Message[] = [...reconciledMessages];

    // Completed optimistic draft (user + assistant)
    if (completedUserMessageObj) {
      const hasAlready = messages.some(
        (m) => m.id === completedUserMessageObj.id
      );
      if (!hasAlready) {
        const backendAssistantIndex = draftAck.assistant
          ? messages.findIndex((message) => message.id === draftAck.assistant?.id)
          : -1;
        if (backendAssistantIndex === -1) messages.push(completedUserMessageObj);
        else messages.splice(backendAssistantIndex, 0, completedUserMessageObj);
      }
    }
    if (completedAssistantMessageObj) {
      const hasAlready = messages.some(
        (m) => m.id === completedAssistantMessageObj.id
      );
      if (!hasAlready) messages.push(completedAssistantMessageObj);
    }

    // Pending user message ekle (ama sadece zaten pending yoksa)
    if (pendingUserMessageObj) {
      const hasPendingAlready = messages.some(
        (msg) => msg.id === pendingUserMessageObj.id
      );
      if (!hasPendingAlready) {
        messages.push(pendingUserMessageObj);
      }
    }

    // Streaming message ekle (ama sadece backend'de henüz yoksa)
    if (streamingMessage) {
      const isDuplicate = messages.some(
        (msg) => msg.id === streamingMessage.id
      );
      if (!isDuplicate) {
        messages.push(streamingMessage);
      }
    }

    return messages;
  }, [
    reconciledMessages,
    draftAck.assistant,
    completedUserMessageObj,
    completedAssistantMessageObj,
    pendingUserMessageObj,
    streamingMessage,
  ]);

  // ===== Message Actions Handler =====
  const { handleMessageAction } = useMessageHandlers(allMessages);

  // ===== Smart Chat Scroll Hook (normal DOM list) =====
  const {
    containerRef: messagesContainerRef,
    attachContainer,
    attachContent,
    bottomRef: messagesEndRef,
    scrollToBottom,
    showScrollToBottom,
  } = useChatScroll({
    messages: allMessages,
    pendingUserMessage,
    chatKey,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    setShowLoadingIndicator,
  });

  // NOT: İlk yükleme scroll'u ve pending-mesaj scroll'u use-chat-scroll
  // hook'unun içinde yönetiliyor; burada duplike effect tutulmuyor.

  // Kuyruğa yeni mesaj eklendiğinde en alta kaydır (kuyruk mesajları
  // allMessages dışında render edildiği için scroll hook'u bunları görmez)
  const queuedCount = queuedMessages.length;
  const prevQueuedCountRef = useRef(0);
  useEffect(() => {
    if (queuedCount > prevQueuedCountRef.current) {
      scrollToBottom();
    }
    prevQueuedCountRef.current = queuedCount;
  }, [queuedCount, scrollToBottom]);

  // ===== Status/Reasoning Display Logic =====
  // Content gelmeye başladığında status/reasoning gizle
  // 🆕 Sadece aktif streaming sırasında göster (tamamlandığında gizle)
  const showStatusAndReasoning = useMemo(
    () => isStreaming && !streamingContent,
    [isStreaming, streamingContent]
  );

  // ===== Clear session when backend message arrives =====
  useEffect(() => {
    const hasDraft = session ? hasCompletedDraft(session) : false;
    if (!hasDraft) return;
    if (draftAck.complete) {
      // Beklenen draft id'si veriliyor: ack hesaplandıktan sonra yeni bir tur
      // tamamlanıp kendi draft'ını yazdıysa onu silmeyelim.
      clearCompletedDraft(chatKey, completedAssistantMessageId ?? undefined);
      if (chatId && queuedMessages.length > 0) {
        void drainQueuedMessages(chatId);
      }
    }
  }, [
    session,
    completedAssistantMessageId,
    draftAck.complete,
    clearCompletedDraft,
    chatKey,
    chatId,
    queuedMessages.length,
    drainQueuedMessages,
  ]);

  // Başka bir sohbete veya sayfaya geçmek, bu sohbetin bekleyen redirect
  // intent'ini iptal eder. Stream arka planda sürse bile aktif olmayan sohbet
  // kullanıcıyı başka bir sayfaya taşıyamaz.
  useEffect(() => () => cancelRedirect(chatKey), [chatKey]);

  // ===== Conversation Not Found Error: Navigate to main root =====
  useEffect(() => {
    if (isError && allMessages.length === 0 && !shouldShowStreamingContent) {
      const errorObj = error as any;
      // Check multiple possible error structures
      const errorCode =
        errorObj?.code ||
        errorObj?.response?.data?.code ||
        errorObj?.response?.data?.error?.code ||
        errorObj?.response?.data?.errorCode ||
        (errorObj?.response?.data && typeof errorObj.response.data === 'object' && !errorObj.response.data.data ? errorObj.response.data.code : null);

      // Check if it's conversation_not_found error
      if (errorCode === "conversation_not_found") {
        // Navigate to main root after a short delay to show error message
        const timer = setTimeout(() => {
          navigate("/");
        }, 2000); // 2 saniye sonra yönlendir
        return () => clearTimeout(timer);
      }
    }
  }, [isError, error, allMessages.length, shouldShowStreamingContent, navigate]);

  // ===== Loading State =====
  if (isLoadingMessages && allMessages.length === 0) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <LoadingSpinner size="lg" text={t("chat.loading")} />
      </div>
    );
  }

  // ===== Conversation Not Found Error State =====
  // Handle conversation_not_found error from API
  if (isError && allMessages.length === 0 && !shouldShowStreamingContent) {
    const errorObj = error as any;
    // Check multiple possible error structures
    const errorCode =
      errorObj?.code ||
      errorObj?.response?.data?.code ||
      errorObj?.response?.data?.error?.code ||
      errorObj?.response?.data?.errorCode ||
      (errorObj?.response?.data && typeof errorObj.response.data === 'object' && !errorObj.response.data.data ? errorObj.response.data.code : null);

    // Check if it's conversation_not_found error
    if (errorCode === "conversation_not_found") {
      return (
        <div className="flex flex-col h-full items-center justify-center">
          <EmptyState
            icon={
              <AlertCircle className="h-6 w-6 text-text-secondary dark:text-gray-400" />
            }
            title={t("chat.conversationNotFound")}
            description={t("chat.conversationNotFoundDescription")}
            action={
              <Button variant="outline" asChild>
                <Link to="/">{t("common.home")}</Link>
              </Button>
            }
          />
        </div>
      );
    }
  }

  // ===== Shared Link Error State =====
  if (
    shareToken &&
    isError &&
    allMessages.length === 0 &&
    !shouldShowStreamingContent
  ) {
    const status = (error as any)?.response?.status;
    const title =
      status === 401 || status === 403
        ? t("chat.shareView.noAccessTitle")
        : t("chat.shareView.invalidTitle");
    const description =
      status === 401 || status === 403
        ? t("chat.shareView.noAccessDescription")
        : t("chat.shareView.invalidDescription");

    return (
      <div className="flex flex-col h-full items-center justify-center">
        <EmptyState
          icon={
            <AlertCircle className="h-6 w-6 text-text-secondary dark:text-gray-400" />
          }
          title={title}
          description={description}
          action={
            <Button variant="outline" asChild>
              <Link to="/">{t("common.home")}</Link>
            </Button>
          }
        />
      </div>
    );
  }

  // ===== Empty State =====
  // 🆕 Streaming content yoksa ve mesaj yoksa empty state göster
  if (allMessages.length === 0 && !shouldShowStreamingContent) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <EmptyState
          title={t("chat.emptyStateTitle")}
          description={t("chat.emptyStateDescription")}
        />
      </div>
    );
  }

  // ===== Main Render =====
  return (
    <div className="flex flex-col h-full relative">
      <div className="relative flex-1 overflow-hidden">
        {/* Messages Container - normal DOM list */}
        <div
          ref={attachContainer}
          className="flex h-full flex-col overflow-x-hidden overflow-y-auto overscroll-contain scrollbar-hide"
        >
          <div
            ref={attachContent}
            className={cn(
              "@container/chat space-y-4 sm:space-y-6 w-full sm:px-4 pt-3 sm:pt-4",
              compactBubbles && "chat-bubbles-fill",
            )}
            style={{ paddingBottom: `${inputHeight || 128}px` }}
          >
            {/* Infinite Scroll Loading Indicator - ChatGPT-style (top) */}
            {(isFetchingNextPage || showLoadingIndicator) && (
              <div className="flex justify-center py-4 mb-4">
                <LoadingSpinner
                  size="sm"
                  text={
                    isFetchingNextPage
                      ? t("chat.loadingMore") ||
                        "Daha fazla mesaj yükleniyor..."
                      : t("chat.preparingMore") || "Hazırlanıyor..."
                  }
                />
              </div>
            )}

            {/* Messages - oldest to newest */}
            {allMessages.map((message, index) => {
              const isLastMessage = index === allMessages.length - 1;
              const isLastAssistantMessage =
                isLastMessage && message.role === "assistant";

              return (
                <MessageErrorBoundary key={message.id}>
                  <MessageRow
                    message={message}
                    isLastAssistantMessage={isLastAssistantMessage}
                    onAction={handleMessageAction}
                  />
                </MessageErrorBoundary>
              );
            })}

            {/* Status Messages */}
            {showStatusAndReasoning && statusMessages.length > 0 && (
              <div className="flex justify-start">
                <div
                  data-chat-bubble="assistant"
                  className={cn(CHAT_BUBBLE_NARROW, "px-3 py-2.5")}
                >
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground flex-shrink-0" />
                    <ShinyText
                      text={statusMessages[statusMessages.length - 1].content}
                      speed={3}
                      className="text-sm font-medium"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Reasoning Messages */}
            {showStatusAndReasoning && reasoningContent && (
              <div className="flex justify-start">
                <div
                  data-chat-bubble="assistant"
                  className={cn(
                    CHAT_BUBBLE_NARROW,
                    "max-h-[100px] overflow-y-auto animate-pulse",
                  )}
                >
                  <div className="flex items-start gap-2">
                    <div className="text-xs text-purple-600 dark:text-purple-400 font-medium mt-0.5">
                      🤔
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-purple-600 dark:text-purple-400 font-medium mb-1">
                        {t("chat.thinking")}
                      </p>
                      <p className="text-sm text-purple-800 dark:text-purple-200 whitespace-pre-wrap">
                        {reasoningContent}
                      </p>
                      <div className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                        <span className="inline-block w-2 h-2 bg-purple-500 rounded-full animate-pulse mr-2"></span>
                        Processing...
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Web product cards (stream sırasında hemen görünür) */}
            {webProducts.length > 0 && (
              <div className="flex justify-start">
                <div
                  data-chat-bubble="assistant"
                  className={cn(CHAT_BUBBLE_WIDTH, "w-full px-4")}
                >
                  <WebProductCarouselWidget
                    products={webProducts}
                    query={webSearchQuery}
                  />
                </div>
              </div>
            )}

            {/* External Link Actions — history ile aynı bileşen */}
            <MessageExternalLinks links={externalLinks} />
            {comparison && <ComparisonButton comparison={comparison} />}

            {/* Sırada bekleyen mesajlar (stream bitince otomatik gönderilir) */}
            {queuedMessages.length > 0 && (
              <div className="flex flex-col items-end gap-3 px-4">
                {queuedMessages.map((queued) => (
                  <div
                    key={queued.id}
                    data-chat-bubble="user"
                    className={cn(
                      CHAT_BUBBLE_WIDTH,
                      "flex items-center justify-end gap-2",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => removeQueuedMessage(chatKey, queued.id)}
                      className="flex-none p-1.5 rounded-full text-muted-foreground/70 hover:text-foreground hover:bg-bg-secondary dark:hover:bg-gray-700 transition-colors"
                      aria-label={t("chat.queue.remove")}
                      title={t("chat.queue.remove")}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                    <div className="min-w-0 opacity-60">
                      {queued.attachmentImageUrls.length > 0 && (
                        <div className="flex justify-end gap-1.5 mb-1.5">
                          {queued.attachmentImageUrls.map((url, urlIndex) => (
                            <img
                              key={`${queued.id}_img_${urlIndex}`}
                              src={url}
                              alt=""
                              className="h-12 w-12 rounded-lg object-cover"
                            />
                          ))}
                        </div>
                      )}
                      <div className="bg-bg-secondary rounded-bl-[12px] rounded-br-[12px] rounded-tl-[12px] px-4 py-2">
                        <p className="m-0 text-md font-normal text-foreground whitespace-pre-wrap break-words">
                          {queued.message}
                        </p>
                      </div>
                      <div className="mt-1 flex items-center justify-end gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {t("chat.queue.pending")}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Bottom anchor */}
            <div ref={messagesEndRef} className="h-3" />
          </div>
        </div>
        <GradientScrollbar scrollRef={messagesContainerRef} />
      </div>

      {/* Scroll To Bottom Button */}
      {showScrollToBottom && (
        <Button
          variant="secondary"
          size="icon"
          className="absolute bottom-28 left-1/2 md:left-2.7/4 -translate-x-1/2 z-50 rounded-full shadow-lg w-10 h-10"
          onClick={() => scrollToBottom()}
          title={t("chat.scrollToBottom")}
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};
